import React from 'react';
import { ArrowUpDown, Filter, X, Share2, Download, Trash2 } from 'lucide-react';

export type ViewMode = 'cards' | 'table';
export type SortOption = 'default' | 'name_asc' | 'name_desc' | 'match_desc' | 'match_asc';
export type FilterOption = 'all' | 'full_matches' | 'partial_matches' | 'no_matches';

interface MobileDatasetHeaderProps {
  tableName?: string;
  leadCount: number;
  isLoading: boolean;
  showFullTitle: boolean;
  onToggleFullTitle: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sortOption: SortOption;
  filterOption: FilterOption;
  onOpenSortMenu: () => void;
  onOpenFilterMenu: () => void;
  onOpenExportMenu: () => void;
  onClearFilters: () => void;
  onShare?: () => void;
  onDelete?: () => void;
  hasExportOptions: boolean;
}

export const MobileDatasetHeader: React.FC<MobileDatasetHeaderProps> = ({
  tableName,
  leadCount,
  isLoading,
  showFullTitle,
  onToggleFullTitle,
  viewMode,
  onViewModeChange,
  sortOption,
  filterOption,
  onOpenSortMenu,
  onOpenFilterMenu,
  onOpenExportMenu,
  onClearFilters,
  onShare,
  onDelete,
  hasExportOptions,
}) => {
  const hasActiveFilters = sortOption !== 'default' || filterOption !== 'all';

  return (
    <div className="flex-shrink-0 bg-white border-b border-gray-200/50 relative px-4 py-3">
      {/* Row 1: Title (full width, tappable to expand) */}
      <button
        onClick={onToggleFullTitle}
        className="w-full text-left active:opacity-70"
      >
        <h1 className={`text-[15px] font-semibold text-gray-900 ${showFullTitle ? 'whitespace-normal' : 'truncate'}`}>
          {tableName || 'Results'}
        </h1>
      </button>

      {/* Row 2: Result count + Action buttons */}
      <div className="flex items-center justify-between mt-1">
        {!isLoading && leadCount > 0 && (
          <span className="text-[12px] text-gray-400">
            {leadCount} result{leadCount !== 1 ? 's' : ''}
          </span>
        )}
        {(isLoading || leadCount === 0) && <span />}

        {/* Action buttons */}
        <div className="flex items-center gap-0.5">
          {onShare && (
            <button
              onClick={onShare}
              className="p-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-lg transition-colors"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
          {hasExportOptions && (
            <button
              onClick={onOpenExportMenu}
              className="p-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-lg transition-colors"
              title="Export"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-500 active:bg-gray-100 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Row 3: View toggle, Sort, Filter */}
      {!isLoading && leadCount > 0 && (
        <div className="flex items-center justify-between mt-2.5">
          {/* Left: View toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-tour="mobile-view-toggle">
            <button
              onClick={() => onViewModeChange('cards')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => onViewModeChange('table')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Table
            </button>
          </div>

          {/* Right: Sort, Filter */}
          <div className="flex items-center gap-1.5">
            {/* Sort */}
            <button
              onClick={onOpenSortMenu}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
                sortOption !== 'default' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <ArrowUpDown className="w-3 h-3" />
              Sort
            </button>

            {/* Filter */}
            <button
              onClick={onOpenFilterMenu}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
                filterOption !== 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Filter className="w-3 h-3" />
              Filter
            </button>

            {/* Clear */}
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="p-1 text-gray-400 active:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
